import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import stats from "./content/site-stats.json";
import { useMeta } from "../hooks/useMeta";
import { draftPath } from "./DraftLayout";
import { Shell } from "./parts";

export default function NotYet() {
  useMeta({ title: "Not yet | Smart Marketer Agency", description: "You are not at the stage where our model works yet. Here is what to do in the meantime." });
  return (
    <main data-testid="draft-not-yet">
      <section className="bg-navy">
        <div className="mx-auto max-w-6xl px-5 py-16 md:py-24">
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-pink">Fit check</div>
          <h1 className="mt-4 text-4xl font-bold leading-[1.08] text-white sm:text-5xl lg:text-[56px]">Not a fit yet. But soon.</h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/75 md:text-lg">
            Right now you are spending under $30k a month on ads and doing under $150k a month in revenue. Our model works when there is enough spend and revenue for a dedicated team to move the needle, and at this stage a $10,000 retainer would eat the profit we are supposed to add.
          </p>
        </div>
      </section>

      <Shell testId="not-yet-body">
        <div data-testid="not-yet-fit-anyway" className="rounded-2xl border-2 border-navy p-8 md:p-10">
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-pink">Think we're a fit anyway?</div>
          <div className="mt-4 grid gap-6 md:grid-cols-[1.4fr_auto] md:items-center">
            <p className="text-base leading-relaxed text-navy/80 md:text-lg">
              These two questions only see your online numbers. If most of your revenue runs through retail, wholesale or Amazon, or you are about to scale and want the team in place first, skip the line and book a call with Pep. Every application is read by a person.
            </p>
            <a href={stats.links.calendly} target="_blank" rel="noopener noreferrer" data-testid="not-yet-book-call" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-navy px-8 py-4 text-base font-bold text-white transition-colors hover:bg-pink">
              Book a call with Pep <ArrowRight size={18} />
            </a>
          </div>
        </div>
        <div className="mt-14 grid gap-12 lg:grid-cols-2">
          <div>
            <h2 className="text-2xl font-bold text-navy md:text-3xl">Not there yet? Here's what we suggest.</h2>
            <p className="mt-5 text-base leading-relaxed text-navy/80 md:text-lg">
              The fastest way to get to the line is to learn the system yourself, with people who are a few steps ahead of you. That is what the Smart Marketer mastermind is for: brand owners, monthly hot seats, and the same playbooks our team runs on client accounts.
            </p>
            <a href={stats.links.mastermind} target="_blank" rel="noopener noreferrer" data-testid="not-yet-mastermind-link" className="mt-8 inline-flex items-center gap-2 rounded-full bg-pink px-8 py-4 text-base font-bold text-white transition-colors hover:bg-pink-dark">
              Look at the mastermind <ArrowRight size={18} />
            </a>
          </div>
          <div className="rounded-2xl bg-fog p-8 md:p-10">
            <div className="text-xs font-bold uppercase tracking-widest text-navy/60">Come back when</div>
            <ul className="mt-4 space-y-3 text-base text-navy">
              <li className="flex gap-3"><span className="font-extrabold text-green">+</span>You are spending $30k a month or more on ads, or</li>
              <li className="flex gap-3"><span className="font-extrabold text-green">+</span>You are doing $150k a month or more in revenue ($2M a year)</li>
            </ul>
            <p className="mt-6 text-sm leading-relaxed text-navy/70">Your details are saved. When you cross the line, <Link to={draftPath("/apply")} className="font-bold text-link hover:underline">run the fit check again</Link> and you will go straight to booking a call with Pep.</p>
            <p className="mt-6 text-sm text-navy/60">Questions in the meantime? <a href={`mailto:${stats.email}`} className="font-bold text-link">{stats.email}</a></p>
          </div>
        </div>
      </Shell>
    </main>
  );
}
